import React, { useMemo, useCallback, useEffect } from 'react'
import { useStateValue } from '@State'
import { saveAnswer } from '../../api'
import { useDragAndDrop } from './utils/useDragAndDrop'
import { FeedbackDisplay } from './utils/FeedbackDisplay'
import { TabSplitLayout } from './utils/TabSplitLayout'
import { SaveIndicator } from './utils/SaveIndicator'
import { FEEDBACK_LABELS, ZONE_ROW, ZONE_BADGE } from './utils/feedbackStyles'
import { stripHtml } from './utils/stripHtml'
import { parseDragDropAnswer } from './utils/dragDropAnswer'

/* Pseudo-class and drag states that cannot be done with inline styles */
const DRAGDROP_STYLES = `
.ngn-dragdrop-option.placed {
  opacity: 0.4;
  pointer-events: none;
}
.ngn-dragdrop-dropzone.drag-over {
  border-color: #007bff;
  background-color: #e3f2fd;
}
`

const BLOCK_TAG_RE = /<\/?(?:p|div|h[1-6]|ul|ol|li|blockquote|pre|table|tr|td|th|thead|tbody|tfoot|section|article|aside|header|footer|nav|figure|figcaption|main|details|summary|dl|dt|dd)[^>]*>/gi

const OPTION_STYLE = {
  padding: '0.25rem 0.5rem', margin: '0.1rem',
  border: '2px solid #9ca3af', backgroundColor: '#fff', cursor: 'grab',
  borderRadius: '6px', transition: 'all 0.2s ease', display: 'inline-block',
  userSelect: 'none', boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  width: '100%', textAlign: 'center',
}

const DROPZONE_BASE = {
  minHeight: '1.5rem', minWidth: '80px',
  display: 'inline-flex', flexDirection: 'column',
  padding: '0.2rem', margin: '2px',
  border: '2px dashed #ddd', borderRadius: '8px',
  backgroundColor: '#fefefe', transition: 'all 0.2s ease',
  cursor: 'default', verticalAlign: 'middle',
}

const ZONE_OPTION_STYLE = {
  padding: '0.25rem 0.5rem', border: '2px solid #9ca3af',
  backgroundColor: '#fff', borderRadius: '8px',
  userSelect: 'none', boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  margin: 'calc(-0.2rem - 2px)',
  width: 'calc(100% + 0.4rem + 4px)', textAlign: 'center',
  boxSizing: 'border-box',
}

const ZONE_EMPTY_STYLE = {
  color: '#999', fontStyle: 'italic', fontSize: '0.9em', padding: '0.25rem 0.5rem',
}

const ZONE_FB_CORRECT = { ...DROPZONE_BASE, border: '2px solid #28a745', backgroundColor: 'transparent' }
const ZONE_FB_WRONG = { ...DROPZONE_BASE, border: '2px solid #d4a017', backgroundColor: 'transparent' }

const BLOCK_OPTIONS_STYLE = {
  display: 'flex', flexDirection: 'column', alignItems: 'center',
  minHeight: '60px', padding: '8px', borderRadius: '8px',
  backgroundColor: '#f5f5f5', gap: '5px',
}

/**
 * Parse a prompt string containing {0}, {1}, {region} etc. into segments.
 */
function parsePrompt(prompt) {
  const parts = []
  const regex = /\{(\w+)\}/g
  const occurrenceCounts = {}
  let lastIndex = 0
  let match
  while ((match = regex.exec(prompt)) !== null) {
    if (match.index > lastIndex) {
      parts.push({ type: 'html', value: prompt.slice(lastIndex, match.index).replace(BLOCK_TAG_RE, ' ').trim() })
    }
    const fieldId = match[1]
    const occurrence = occurrenceCounts[fieldId] || 0
    occurrenceCounts[fieldId] = occurrence + 1
    parts.push({ type: 'field', fieldId, occurrence })
    lastIndex = regex.lastIndex
  }
  if (lastIndex < prompt.length) {
    parts.push({ type: 'html', value: prompt.slice(lastIndex).replace(BLOCK_TAG_RE, ' ').trim() })
  }
  return parts
}

function placementsToAnswer(placements) {
  const answer = {}
  for (const [zoneId, optGuid] of Object.entries(placements)) {
    const fieldId = zoneId.split(':')[1]
    if (!answer[fieldId]) answer[fieldId] = []
    answer[fieldId].push(optGuid)
  }
  const result = {}
  for (const [fieldId, guids] of Object.entries(answer)) {
    result[fieldId] = guids.join(' ')
  }
  return JSON.stringify(result)
}

function answerToPlacements(answer, promptParts) {
  const placements = {}
  const parsed = parseDragDropAnswer(answer)

  const filled = {}
  for (const part of promptParts) {
    if (part.type !== 'field') continue
    const fieldId = part.fieldId
    const guids = parsed[fieldId]
    if (!guids) continue
    const guidList = guids.trim().split(' ').filter(Boolean)
    const idx = filled[fieldId] || 0
    if (idx < guidList.length) {
      placements[`f:${fieldId}:${part.occurrence}`] = guidList[idx]
    }
    filled[fieldId] = idx + 1
  }
  return placements
}

export const NGNDragDropQuestion = ({ question, renderTabs = true }) => {
  const [state, dispatch] = useStateValue()
  const { coursenum, activitySubmissionId, savingGuids, readonly } = state
  const isSaving = savingGuids.has(question.guid)

  useEffect(() => {
    const parsedAnswer = parseDragDropAnswer(question.answer)
    const selected = question.data.dragdropFields
      .filter(f => (parsedAnswer[f.id] || '').trim()).length
    const { minSelections, maxSelections } = question.data
    dispatch({
      type: 'SET_FULLY_ANSWERED', guid: question.guid,
      value: selected >= minSelections && selected <= maxSelections
    })
  }, [question.answer])

  const promptParts = useMemo(
    () => parsePrompt(question.data.prompt || ''),
    [question.data.prompt]
  )

  const fieldToBlock = useMemo(() => {
    const map = {}
    for (const f of question.data.dragdropFields) {
      map[f.id] = f.blockGuid
    }
    return map
  }, [question.data.dragdropFields])

  const initialPlacements = useMemo(
    () => answerToPlacements(question.answer, promptParts),
    [question.answer, promptParts]
  )

  const saveNewAnswer = useCallback(async (newPlacements) => {
    const answerJson = placementsToAnswer(newPlacements)
    dispatch({ type: 'SET_ANSWER', guid: question.guid, answer: answerJson })
    dispatch({ type: 'SAVE_START', guid: question.guid })
    try {
      const response = await saveAnswer(coursenum, activitySubmissionId, question.guid, answerJson)
      dispatch({ type: 'SAVE_SUCCESS', guid: question.guid, payload: response.data })
    } catch (e) {
      dispatch({ type: 'SAVE_ERROR', guid: question.guid })
    }
  }, [coursenum, activitySubmissionId, question.guid, dispatch])

  const canDrop = useCallback((optGuid, zoneId) => {
    const opt = question.data.dragdropOptions.find(o => o.guid === optGuid)
    if (!opt) return false
    const fieldId = zoneId.split(':')[1]
    return fieldToBlock[fieldId] === opt.blockGuid
  }, [question.data.dragdropOptions, fieldToBlock])

  const {
    placements, selectedGuid, dragOverZone,
    selectOption, handleZoneClick,
    handleDragStart, handleDragEnd, handleDragOver, handleDragLeave, handleDrop,
    handleOptionKeyDown, handleZoneKeyDown,
    reset, isPlaced,
  } = useDragAndDrop({
    initialPlacements,
    readonly: readonly || isSaving,
    canDrop,
    onPlacementsChange: saveNewAnswer,
  })

  const handleReset = useCallback(async () => {
    if (readonly || isSaving) return
    reset()
  }, [readonly, isSaving, reset])

  const optionTextMap = useMemo(() => {
    const map = {}
    for (const opt of question.data.dragdropOptions) {
      map[opt.guid] = opt.text
    }
    return map
  }, [question.data.dragdropOptions])

  const { showFeedback } = state
  const { showAnswer } = state.feedbackSettings
  const { correctAnswer } = question
  const showZoneFeedback = showFeedback && showAnswer && correctAnswer

  // Compute the expected guid for each feedback zone.
  const correctByZone = useMemo(() => {
    if (!showZoneFeedback) return {}
    const byZone = {}
    const fieldOccFilled = {}
    for (const part of promptParts) {
      if (part.type !== 'field') continue
      const guids = correctAnswer[part.fieldId]
      if (!guids) continue
      const idx = fieldOccFilled[part.fieldId] || 0
      fieldOccFilled[part.fieldId] = idx + 1
      const cGuid = Array.isArray(guids) ? guids[idx] : null
      const zoneId = `f:${part.fieldId}:${part.occurrence}`
      byZone[zoneId] = cGuid
    }
    return byZone
  }, [showZoneFeedback, correctAnswer, promptParts])

  // Compute min-width per block from the longest option text
  const blockMinWidths = useMemo(() => {
    const widths = {}
    for (const block of question.data.dragdropBlocks) {
      const opts = question.data.dragdropOptions.filter(o => o.blockGuid === block.guid)
      const maxLen = opts.reduce((max, o) => Math.max(max, o.text.length), 0)
      widths[block.guid] = (maxLen + 8) + 'ch'
    }
    return widths
  }, [question.data.dragdropBlocks, question.data.dragdropOptions])

  return (
    <>
      <FeedbackDisplay question={question} />
      <style>{DRAGDROP_STYLES}</style>
      <div className="position-relative">
        <SaveIndicator questionGuid={question.guid} />
      </div>
      <TabSplitLayout question={question} renderTabs={renderTabs}>
      <div style={{ width: '100%' }}>
        <div
          role="heading"
          aria-level={3}
          className="mb-2 text-left"
          dangerouslySetInnerHTML={{ __html: question.text }}
        />

        <div className='d-flex flex-wrap align-items-center' style={{ width: '100%', gap: '0.1rem' }}>
          {promptParts.map((part, i) => {
            if (part.type === 'html') {
              return part.value ? <span key={i} dangerouslySetInnerHTML={{ __html: part.value }} /> : null
            }
            const zoneId = `f:${part.fieldId}:${part.occurrence}`
            const placedGuid = placements[zoneId]

            if (showZoneFeedback) {
              const correctGuid = correctByZone[zoneId]
              const isCorrectPlacement = placedGuid && placedGuid === correctGuid

              return (
                <span key={i} style={isCorrectPlacement ? ZONE_FB_CORRECT : ZONE_FB_WRONG}>
                  {isCorrectPlacement ? (
                    <span style={ZONE_ROW.correct}>
                      <span dangerouslySetInnerHTML={{ __html: optionTextMap[placedGuid] || '' }} />
                      <span style={ZONE_BADGE.correct}>{FEEDBACK_LABELS.correct}</span>
                    </span>
                  ) : (
                    <>
                      {placedGuid && (
                        <span style={ZONE_ROW.incorrect}>
                          <span dangerouslySetInnerHTML={{ __html: optionTextMap[placedGuid] || '' }} />
                          <span style={ZONE_BADGE.incorrect}>{FEEDBACK_LABELS.incorrect}</span>
                        </span>
                      )}
                      {correctGuid && (
                        <span style={ZONE_ROW.missed}>
                          <span dangerouslySetInnerHTML={{ __html: optionTextMap[correctGuid] || '' }} />
                          <span style={ZONE_BADGE.missed}>{FEEDBACK_LABELS.missed}</span>
                        </span>
                      )}
                    </>
                  )}
                </span>
              )
            }

            const isOver = dragOverZone === zoneId
            const isValidTarget = selectedGuid && canDrop(selectedGuid, zoneId)
            const blockGuid = fieldToBlock[part.fieldId]
            const zoneMinWidth = blockGuid && blockMinWidths[blockGuid] ? blockMinWidths[blockGuid] : undefined
            return (
              <span
                key={i}
                className={[
                  'ngn-dragdrop-dropzone',
                  isOver ? 'drag-over' : '',
                  isValidTarget ? 'valid-target' : '',
                  selectedGuid ? 'has-selection' : '',
                ].filter(Boolean).join(' ')}
                style={{ ...DROPZONE_BASE, ...(zoneMinWidth && { minWidth: zoneMinWidth }) }}
                role="listbox"
                tabIndex={0}
                aria-label={`Drop zone for field ${part.fieldId}`}
                onClick={() => handleZoneClick(zoneId)}
                onKeyDown={e => handleZoneKeyDown(e, zoneId)}
                onDragOver={e => handleDragOver(e, zoneId)}
                onDragLeave={e => handleDragLeave(e, zoneId)}
                onDrop={e => handleDrop(e, zoneId)}
              >
                {placedGuid ? (
                  <span style={ZONE_OPTION_STYLE} dangerouslySetInnerHTML={{ __html: optionTextMap[placedGuid] || '' }} />
                ) : (
                  <span style={ZONE_EMPTY_STYLE}>Unanswered</span>
                )}
              </span>
            )
          })}
        </div>

        <div className="d-flex" style={{ gap: '10px', marginTop: '8px' }}>
          {question.data.dragdropBlocks.map(block => (
            <div key={block.guid} style={{ minWidth: blockMinWidths[block.guid] }}>
              <h4 className="text-center font-weight-bold" style={{ marginBottom: '4px' }} dangerouslySetInnerHTML={{ __html: block.title || '' }} />
              <div
                style={BLOCK_OPTIONS_STYLE}
                role="listbox"
                aria-label={`Available answers for ${stripHtml(block.title)}`}
              >
                {question.data.dragdropOptions
                  .filter(opt => opt.blockGuid === block.guid)
                  .map(opt => {
                    const placed = isPlaced(opt.guid)
                    const isSelected = selectedGuid === opt.guid
                    return (
                      <div
                        key={opt.guid}
                        className={[
                          'ngn-dragdrop-option',
                          isSelected ? 'selected' : '',
                          placed ? 'placed' : '',
                          readonly ? 'readonly' : '',
                          isSaving ? 'saving' : '',
                        ].filter(Boolean).join(' ')}
                        style={OPTION_STYLE}
                        role="option"
                        tabIndex={placed || readonly ? -1 : 0}
                        draggable={!readonly && !placed && !isSaving}
                        aria-selected={isSelected}
                        aria-label={stripHtml(opt.text)}
                        onClick={() => !placed && selectOption(opt.guid)}
                        onKeyDown={e => !placed && handleOptionKeyDown(e, opt.guid)}
                        onDragStart={e => handleDragStart(e, opt.guid)}
                        onDragEnd={handleDragEnd}
                      >
                        <span dangerouslySetInnerHTML={{ __html: opt.text || '' }} />
                      </div>
                    )
                  })}
              </div>
            </div>
          ))}
        </div>

        {!readonly && (
          <div className="d-flex align-items-center mt-2" style={{ gap: '0.5rem' }}>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={handleReset}
              disabled={isSaving}
            >
              Reset
            </button>
          </div>
        )}
      </div>
      </TabSplitLayout>
    </>
  )
}
